import { useCallback, useState } from 'react'
import dayjs from 'dayjs'

import type { Patient } from '@/core/models/patient.model'
import { InsOperation } from '@/core/models/ins-audit.model'
import { useLazyLookupPatientByInsNIRQuery, useLazySearchInsIdentityPatientsQuery } from '@/core/services/patient'
import { useLogInsCall } from '@/pages/patient/model/useLogInsCall'
import { patientGenderToInsSex } from '@/utils/patient'

import type { ExternalIdentityPatientData } from '../lib/external-identity'
import { classifyCandidate, type CandidateClassification } from '../lib/identity-matching'

export type ReconciliationCandidate = CandidateClassification & { patient: Patient }

export type ReconciliationResult =
  | { status: 'NO_MATCH'; collisionPatient: Patient | null }
  | { status: 'CANDIDATES'; candidates: ReconciliationCandidate[]; collisionPatient: Patient | null }
  | { status: 'ERROR' }

export function useExternalIdentityReconciliation() {
  const [isReconciling, setIsReconciling] = useState(false)
  const [lookupPatientByInsNIR] = useLazyLookupPatientByInsNIRQuery()
  const [searchInsIdentityPatients] = useLazySearchInsIdentityPatientsQuery()
  const logInsCall = useLogInsCall()

  const reconcile = useCallback(
    async (received: ExternalIdentityPatientData, emitter: string): Promise<ReconciliationResult> => {
      setIsReconciling(true)
      try {
        const birthDate = dayjs(received.birthday).isValid() ? dayjs(received.birthday).format('YYYY-MM-DD') : undefined

        const traced = await logInsCall({
          operation: InsOperation.RECEIVE_EXTERNAL_IDENTITY,
          patientFullName: `${received.birthSurname} ${received.birthFirstName}`.trim() || undefined,
          emitter,
          receivedIdentity: {
            insNIR: received.insNIR,
            insOID: received.insOID,
            birthName: received.birthSurname,
            birthGiven: received.birthName,
            birthDate,
            sex: received.gender ? String(patientGenderToInsSex(received.gender)) : undefined,
            birthPlaceCode: received.birthPlaceCode,
          },
        })
        if (!traced) {
          console.warn('[useExternalIdentityReconciliation] The incoming flux could not be traced in the INSi journal.')
        }

        let collisionPatient: Patient | null = null
        try {
          const lookup = await lookupPatientByInsNIR(received.insNIR).unwrap()
          collisionPatient = lookup?.patient ?? null
        } catch {
          collisionPatient = null
        }

        let items: Patient[] = []
        try {
          const search = await searchInsIdentityPatients({
            surname: received.birthSurname,
            givenName: received.birthFirstName,
            birthDate,
            limit: 10,
            skip: 0,
          }).unwrap()
          items = search?.items ?? []
        } catch (error) {
          console.error('[useExternalIdentityReconciliation] Repository search failed:', error)
          return { status: 'ERROR' }
        }

        const candidates = items
          .map((patient) => ({ patient, ...classifyCandidate(received, patient) }))
          .filter(({ kind, divergentTraits }) => kind === 'EXACT' || (!divergentTraits.includes('birthday') && !divergentTraits.includes('birthSurname')))
          .sort((a, b) => a.divergentTraits.length - b.divergentTraits.length)

        if (candidates.length === 0) return { status: 'NO_MATCH', collisionPatient }
        return { status: 'CANDIDATES', candidates, collisionPatient }
      } finally {
        setIsReconciling(false)
      }
    },
    [logInsCall, lookupPatientByInsNIR, searchInsIdentityPatients]
  )

  return { reconcile, isReconciling }
}
